import { useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { useReOS } from "./domain/store";
import { ConnectedChannelManager } from "./ConnectedChannelManager";
import { Alert } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import { Disclosure } from "./components/ui/disclosure";
import { userSafeErrorMessage } from "./lib/userSafeError";

export function EbayReadOnlyConnectionPanel({ canManage = false, workflowLabel, onChangeWorkflow }: {
  embedded?: boolean;
  canManage?: boolean;
  workflowLabel?: string;
  onChangeWorkflow?: () => void;
}) {
  const store = useReOS();
  const status = store.ebayIntegration;
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string>();
  const connected = status?.connection?.status === "connected" && status.authorization.authorized;
  const simulator = status?.environment === "simulated";

  async function run(operation: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(undefined);
    try { await operation(); }
    catch (reason) { setError(userSafeErrorMessage(reason, "The eBay connection could not be loaded. Try again.")); }
    finally { busyRef.current = false; setBusy(false); }
  }

  if (connected) return <ConnectedChannelManager marketplace="ebay" canManage={canManage}
    accountName={simulator ? "Test account" : status.connection?.username || "eBay seller"}
    simulator={simulator} ordersReady={status.notifications.ready} ordersError={Boolean(status.orderSync?.orderError)}
    onRefresh={store.refreshEbayIntegration} onDisconnect={store.disconnectEbay}
    onRetryOrders={() => store.updateEbayIntegration({ verifyNotifications: true })}
    orderCosts={{ available: Boolean(status.finances?.available), authorized: Boolean(status.finances?.authorized),
      onAuthorize: async () => { window.location.assign(await store.startEbayConnection({ financialAccess: true })); } }}
    details={<><p>{marketplaceName(status.marketplaceID)} · {simulator ? "Sample data" : status.accessMode === "read_write" ? "Read, import and update" : "Read and import"}</p><p>{status.accessMode === "read_write" ? "Listing changes are sent after you review and confirm them." : "You can allow listing updates when reviewing a saved edit."}</p>{onChangeWorkflow && <Button variant="ghost" onClick={onChangeWorkflow}>Change workflow</Button>}</>} />;

  return <section className="channel-connect-entry" aria-label="eBay connection">
    {error && <Alert tone="danger">{error}</Alert>}
    {!status ? <><p>Checking connection…</p><Button disabled={busy} loading={busy} loadingLabel="Checking connection" onClick={() => void run(store.refreshEbayIntegration)}>Refresh connection</Button></> : !status.configured ? <p>eBay connections are not available here yet.</p> : <>
      <p>{simulator ? "Try a sample account without contacting eBay." : "Bring listings and paid orders into Listwork. Connecting cannot change eBay."}</p>
      {canManage ? <Button variant="primary" disabled={busy} loading={busy} loadingLabel="Opening eBay" onClick={() => void run(async () => { window.location.assign(await store.startEbayConnection()); })}>{status.connection ? "Reconnect eBay" : simulator ? "Connect test account" : "Continue to eBay"}<ExternalLink data-icon="inline-end" aria-hidden="true" /></Button> : <p>A workspace owner or admin can connect eBay.</p>}
      <Disclosure label="What Listwork can access"><p>Seller identity, active listings, stock shown by eBay, and paid orders. Listwork never sees your eBay password.</p></Disclosure>
    </>}
  </section>;
}

function marketplaceName(id: string) {
  return ({ EBAY_GB: "eBay UK", EBAY_US: "eBay US", EBAY_DE: "eBay Germany", EBAY_FR: "eBay France" } as Record<string, string>)[id] ?? id.replace(/^EBAY_/, "eBay ");
}
